import axios from 'axios'

// 从 cookie 获取值的辅助函数
function getCookie(name) {
  var value = '; ' + document.cookie
  var parts = value.split('; ' + name + '=')
  if (parts.length === 2) {
    return parts.pop().split(';').shift()
  }
  return null
}

var apiBaseUrl = process.env.VUE_APP_API_BASE_URL || 'http://localhost:5000'

var api = axios.create({
  baseURL: apiBaseUrl,
  timeout: 10000
})

// 用于跟踪刷新 token 的状态
let isRefreshing = false
let refreshSubscribers = []

// 添加请求到重试队列
function subscribeTokenRefresh(cb) {
  refreshSubscribers.push(cb)
}

// 执行重试队列中的请求
function onRefreshed(token) {
  refreshSubscribers.forEach(cb => cb(token))
  refreshSubscribers = []
}

// 请求拦截器 - 添加 token 和 CSRF
api.interceptors.request.use(
  config => {
    // 从 localStorage 获取 token，兼容两种 key
    const token = localStorage.getItem('access_token') || localStorage.getItem('token')
    if (token) {
      // 添加 Authorization 请求头
      config.headers['Authorization'] = `Bearer ${token}`
    }

    // 添加 CSRF 令牌（从 cookie 获取）
    // Flask-WTF 期望的 CSRF token cookie 名称通常是 'csrf_token'
    const csrfToken = getCookie('csrf_token')
    if (csrfToken) {
      config.headers['X-CSRFToken'] = csrfToken
    }

    // 调试日志（仅限上传请求）
    if (config.url && config.url.includes('/admin/upload')) {
      console.log('上传请求拦截器 - 请求头:', config.headers)
      console.log('完整请求配置:', {
        method: config.method,
        url: config.url,
        data: config.data instanceof FormData ? 'FormData' : config.data,
        headers: config.headers
      })
    }

    return config
  },
  error => {
    return Promise.reject(error)
  }
)

// 响应拦截器
api.interceptors.response.use(
  response => {
    return response
  },
  async error => {
    const originalRequest = error.config

    // 处理HTTP错误状态码，提供中文提示
    if (error.response) {
      const status = error.response.status
      let message = error.response.data?.message
      const errors = error.response.data?.errors

      // 调试日志 - 显示完整错误响应
      console.log('HTTP错误响应:', {
        status,
        statusText: error.response.statusText,
        data: error.response.data,
        headers: error.response.headers,
        config: {
          url: error.config.url,
          method: error.config.method,
          headers: error.config.headers
        }
      })

      // 如果是400错误且有验证错误详情，提供更详细的错误信息
      if (status === 400 && errors && Array.isArray(errors)) {
        console.log('数据验证错误详情:', errors)
        const errorDetails = errors.map(e => {
          const field = e.loc?.join('.') || '未知字段'
          return `${field}: ${e.msg} (类型: ${e.type})`
        }).join('; ')
        message = `数据验证失败: ${errorDetails}`
        console.log('格式化的验证错误:', message)
      }

      // 检查是否是CSRF错误（可能返回HTML）
      if (status === 400 && !message) {
        const contentType = error.response.headers['content-type']
        if (contentType && contentType.includes('text/html')) {
          // 可能是CSRF错误返回的HTML页面
          message = 'CSRF验证失败，请检查登录状态或刷新页面'
          console.warn('检测到HTML格式的400响应，可能是CSRF错误')
        }
      }

      // 根据不同状态码提供中文错误提示
      const errorMessages = {
        400: message || '请求参数错误，请检查后重试',
        401: message || '未授权，请先登录',
        403: message || '没有权限执行此操作',
        404: message || '请求的资源不存在',
        405: message || '请求方法不允许',
        409: message || '资源冲突，请检查数据',
        413: message || '上传文件过大，请选择更小的文件',
        415: message || '不支持的文件类型',
        500: message || '服务器内部错误，请稍后重试',
        502: message || '服务器网关错误，请稍后重试',
        503: message || '服务器暂时不可用，请稍后重试',
        504: message || '服务器响应超时，请稍后重试'
      }

      // 将中文错误信息附加到error对象上
      error.userMessage = errorMessages[status] || `请求失败（错误码：${status}）`
      // 将验证错误也附加到error对象上
      if (errors) {
        error.validationErrors = errors
      }
    } else if (error.request) {
      // 请求已发出但没有收到响应
      error.userMessage = '网络连接失败，请检查网络后重试'
    } else {
      // 其他错误
      error.userMessage = error.message || '请求失败，请稍后重试'
    }

    // 如果是 401 错误且未重试过
    if (error.response && error.response.status === 401 && !originalRequest._retry) {
      // 如果是登录请求本身返回 401（密码错误），直接返回错误，不要跳转
      if (originalRequest.url.includes('/auth/login')) {
        return Promise.reject(error)
      }

      // 如果是刷新 token 的请求本身返回 401，直接跳转到登录页，避免循环
      if (originalRequest.url.includes('/auth/refresh')) {
        localStorage.removeItem('access_token')
        localStorage.removeItem('token')
        localStorage.removeItem('refresh_token')
        window.location.href = '/admin/login'
        return Promise.reject(error)
      }

      // 检查是否有 refresh_token
      const refreshToken = localStorage.getItem('refresh_token')
      if (!refreshToken) {
        // 没有 refresh token，跳转到登录页
        localStorage.removeItem('access_token')
        localStorage.removeItem('token')
        window.location.href = '/admin/login'
        return Promise.reject(error)
      }

      // 标记重试，防止重复刷新
      originalRequest._retry = true

      if (!isRefreshing) {
        isRefreshing = true
        try {
          // 调用刷新 token 接口
          const response = await axios.post(`${api.defaults.baseURL}/auth/refresh`, {
            refresh_token: refreshToken
          })
          const { access_token, refresh_token } = response.data.data
          // 保存新的 token，同时保存为 access_token 和 token（兼容旧代码）
          localStorage.setItem('access_token', access_token)
          localStorage.setItem('token', access_token)
          if (refresh_token) {
            localStorage.setItem('refresh_token', refresh_token)
          }
          // 更新 axios 默认请求头
          api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`
          // 执行重试队列中的请求
          onRefreshed(access_token)
          // 重试原始请求
          originalRequest.headers['Authorization'] = `Bearer ${access_token}`
          return api(originalRequest)
        } catch (refreshError) {
          // 刷新失败，清除所有 token 并跳转到登录页
          localStorage.removeItem('access_token')
          localStorage.removeItem('token')
          localStorage.removeItem('refresh_token')
          window.location.href = '/admin/login'
          return Promise.reject(refreshError)
        } finally {
          isRefreshing = false
        }
      } else {
        // 已经在刷新 token，将请求加入队列等待刷新完成
        return new Promise(resolve => {
          subscribeTokenRefresh(token => {
            originalRequest.headers['Authorization'] = `Bearer ${token}`
            resolve(api(originalRequest))
          })
        })
      }
    }
    return Promise.reject(error)
  }
)

export { api }
